import React, { useState, useEffect } from 'react';
import './FlashcardsGame.css';

const FlashcardsGame = ({ dictionaryEntries, onClose, studentId }) => {
  // Scoped per student so learned-words/stats don't leak between different
  // student accounts logged in on the same browser (e.g. a shared classroom
  // computer). No persistence at all when studentId is absent/placeholder,
  // matching the guard used by usePaymentBalance.js.
  const canPersist = studentId && studentId !== 'new';
  const learnedWordsKey = canPersist ? `student-learned-words_${studentId}` : null;
  const statsKey = canPersist ? `student-flashcards-stats_${studentId}` : null;

  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stats, setStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [gameStarted, setGameStarted] = useState(false);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [allTimeStats, setAllTimeStats] = useState({ correct: 0, incorrect: 0, gamesPlayed: 0 });
  const [learnedWords, setLearnedWords] = useState(new Set());
  const [showLearnedWords, setShowLearnedWords] = useState(true);

  useEffect(() => {
    if (dictionaryEntries && dictionaryEntries.length > 0) {
      // Load learned words from localStorage
      const savedLearnedWords = learnedWordsKey ? localStorage.getItem(learnedWordsKey) : null;
      if (savedLearnedWords) {
        try {
          setLearnedWords(new Set(JSON.parse(savedLearnedWords)));
        } catch (e) {
          console.error('Error loading learned words:', e);
        }
      }
      
      // Filter words based on showLearnedWords setting
      const filtered = showLearnedWords 
        ? dictionaryEntries 
        : dictionaryEntries.filter(entry => !learnedWords.has(entry.word));
      
      setFilteredEntries(filtered);
      setStats({ correct: 0, incorrect: 0, total: filtered.length });
    }
    
    // Load all-time stats from localStorage
    const savedStats = statsKey ? localStorage.getItem(statsKey) : null;
    if (savedStats) {
      try {
          setAllTimeStats(JSON.parse(savedStats));
      } catch (e) {
        console.error('Error loading flashcards stats:', e);
      }
    }
  }, [dictionaryEntries, showLearnedWords, learnedWords, learnedWordsKey, statsKey]);

  const handleCardClick = () => {
    if (!showAnswer) {
      setShowAnswer(true);
    } else {
      setIsFlipped(!isFlipped);
      setShowAnswer(false);
    }
  };

  const handleSwipe = (direction) => {
    const currentWord = filteredEntries[currentCardIndex].word;
    
    if (direction === 'right') {
      // Know the word - only count if not already learned
      if (!learnedWords.has(currentWord)) {
        setStats(prev => ({ ...prev, correct: prev.correct + 1 }));
        
        // Add to learned words
        const newLearnedWords = new Set(learnedWords);
        newLearnedWords.add(currentWord);
        setLearnedWords(newLearnedWords);
        if (learnedWordsKey) localStorage.setItem(learnedWordsKey, JSON.stringify([...newLearnedWords]));
        
        // Update all-time stats
        setAllTimeStats(prev => ({ ...prev, correct: prev.correct + 1 }));
      }
    } else {
      // Don't know the word
      setStats(prev => ({ ...prev, incorrect: prev.incorrect + 1 }));
      setAllTimeStats(prev => ({ ...prev, incorrect: prev.incorrect + 1 }));
    }
    
    // Move to next card
    if (currentCardIndex < filteredEntries.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
      setIsFlipped(false);
      setShowAnswer(false);
    } else {
      // Game finished - save all-time stats
      if (statsKey) localStorage.setItem(statsKey, JSON.stringify(allTimeStats));
      setGameStarted(false);
    }
  };

  const startGame = () => {
    setGameStarted(true);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setShowAnswer(false);
    setStats({ correct: 0, incorrect: 0, total: filteredEntries.length });
  };

  const resetGame = () => {
    setGameStarted(false);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setShowAnswer(false);
    setStats({ correct: 0, incorrect: 0, total: filteredEntries.length });
  };

  if (!gameStarted) {
    return (
      <div className="student-flashcards-container">
        <div className="student-flashcards-wrapper">
          <button className="student-close-flashcards-btn" onClick={onClose}>
            ✕
          </button>
          <div className="student-flashcards-content">
          <div className="student-game-info">
            <p>Test your knowledge with flashcards!</p>
            <p>Total words: {filteredEntries.length}</p>
            
            <div className="student-game-options">
              <label className="student-toggle-option">
                <input
                  type="checkbox"
                  checked={showLearnedWords}
                  onChange={(e) => setShowLearnedWords(e.target.checked)}
                />
                <span>Show learned words</span>
              </label>
            </div>
            
            {allTimeStats.gamesPlayed > 0 && (
              <div className="student-stats-summary">
                <h4>All-Time Statistics:</h4>
                <p>Games played: {allTimeStats.gamesPlayed}</p>
                <p>Total correct: {allTimeStats.correct}</p>
                <p>Total incorrect: {allTimeStats.incorrect}</p>
                <p>Success rate: {allTimeStats.correct + allTimeStats.incorrect > 0 ? 
                  Math.round((allTimeStats.correct / (allTimeStats.correct + allTimeStats.incorrect)) * 100) : 0}%</p>
              </div>
            )}
          </div>
          
          <button 
            className="student-start-game-btn" 
            onClick={startGame}
            disabled={filteredEntries.length === 0}
          >
            Start Game
          </button>
          
          {allTimeStats.gamesPlayed > 0 && (
            <button 
              className="student-reset-stats-btn"
              onClick={() => {
                setAllTimeStats({ correct: 0, incorrect: 0, gamesPlayed: 0 });
                if (statsKey) localStorage.removeItem(statsKey);
              }}
            >
              Reset Statistics
            </button>
          )}
          </div>
        </div>
      </div>
    );
  }

  if (filteredEntries.length === 0) {
    return (
      <div className="student-flashcards-container">
        <div className="student-flashcards-wrapper">
          <button className="student-close-flashcards-btn" onClick={onClose}>
            ✕
          </button>
          <div className="student-flashcards-content">
          <p>No words in dictionary to play with!</p>
          <button className="student-start-game-btn" onClick={onClose}>
            Back to Dictionary
          </button>
          </div>
        </div>
      </div>
    );
  }

  const currentCard = filteredEntries[currentCardIndex];
  const progress = ((currentCardIndex + 1) / filteredEntries.length) * 100;

  return (
    <div className="student-flashcards-container">
      <div className="student-flashcards-wrapper">
        <button className="student-close-flashcards-btn" onClick={onClose}>
          ✕
        </button>
        <div className="student-flashcards-content">
        <div className="student-progress-bar">
          <div className="student-progress-fill" style={{ width: `${progress}%` }}></div>
          <span className="student-progress-text">
            {currentCardIndex + 1} / {filteredEntries.length}
          </span>
        </div>
        
        <div className="student-card-container">
          <div 
            className={`student-flashcard ${isFlipped ? 'student-flipped' : ''}`}
            onClick={handleCardClick}
          >
            <div className="student-card-front">
              <div className="student-card-content">
                <h2 className="student-word">{currentCard.word}</h2>
                <p className="student-level">Level: {currentCard.level}</p>
                {showAnswer && (
                  <div className="student-hint">
                    <p>Click to see answer</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="student-card-back">
              <div className="student-card-content">
                <h3 className="student-translation">{currentCard.translation}</h3>
                <p className="student-example">{currentCard.example}</p>
                <p className="student-level">Level: {currentCard.level}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="student-swipe-buttons">
          <button 
            className="student-swipe-btn student-swipe-left"
            onClick={() => handleSwipe('left')}
          >
            Don't Know
          </button>
          <button 
            className="student-swipe-btn student-swipe-right"
            onClick={() => handleSwipe('right')}
          >
            Know
          </button>
        </div>
        
        <div className="student-current-stats">
          <span>Correct: {stats.correct}</span>
          <span>Incorrect: {stats.incorrect}</span>
        </div>
        
        <button className="student-reset-game-btn" onClick={resetGame}>
          Reset Game
        </button>
        </div>
      </div>
    </div>
  );
};

export default FlashcardsGame;
